import type {
  CreatePaymentInput,
  ListQuery,
  Paged,
  PaymentRequest,
  PaymentStatus,
} from '../api/payments';
import type { CashVoucher, CreateCashVoucherInput } from '../api/cash-vouchers';

/**
 * Mock localStorage-backed API cho Payment Requests.
 * Chỉ active khi dev mode + dev-user login (không có DB).
 * Xoá `erp_ac_dev_user` từ localStorage → tắt mock, dùng real API.
 */
const KEY = 'erp_ac_mock_payments';
const HISTORY_KEY = 'erp_ac_mock_payments_history';

export function isMockMode(): boolean {
  if (!import.meta.env.DEV) return false;
  // Bật `erp_ac_use_real_api=1` để tắt mock và bay lên BE thật (cần API + Postgres chạy).
  if (localStorage.getItem('erp_ac_use_real_api') === '1') return false;
  return localStorage.getItem('erp_ac_dev_user') !== null;
}

/** Bảng tra tên NV theo id — khớp seed dev + hardcoded picker. */
const MOCK_EMP_NAMES: Record<string, string> = {
  'dev-ktt': 'Nguyễn Thị Hà',
  'dev-nvkt': 'Lê Huỳnh Diệu',
  'dev-ceo': 'Phạm Minh Đức',
  'dev-nv': 'Trần Thị B',
};

function readAll(): PaymentRequest[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const items = JSON.parse(raw) as PaymentRequest[];
      // Mock cũ chuyển thẳng KTT. Không có phiếu chi thì phải quay về hàng chờ NV.KT.
      const normalized = items.map((item) => item.status === 'PENDING_KTT' && !item.cashVoucher
        ? { ...item, status: 'PENDING_NVKT' as PaymentStatus }
        : item);
      if (normalized.some((item, index) => item.status !== items[index].status)) writeAll(normalized);
      return normalized;
    }
  } catch {
    /* fallthrough */
  }
  // Seed lần đầu
  const seed = seedData();
  localStorage.setItem(KEY, JSON.stringify(seed));
  return seed;
}

function writeAll(items: PaymentRequest[]): void {
  localStorage.setItem(KEY, JSON.stringify(items));
}

function currentUser() {
  try {
    return JSON.parse(localStorage.getItem('erp_ac_dev_user') || 'null');
  } catch {
    return null;
  }
}

function genCode(): string {
  const now = new Date();
  const yyyymm = `${now.getFullYear()}${(now.getMonth() + 1).toString().padStart(2, '0')}`;
  const prefix = `PC-${yyyymm}-`;
  const items = readAll();
  const existing = items.filter((i) => i.code.startsWith(prefix)).length;
  return `${prefix}${(existing + 1).toString().padStart(4, '0')}`;
}

function addHistory(prId: string, action: string, fromStatus: string | null, toStatus: string | null, note?: string) {
  const user = currentUser();
  const key = `${HISTORY_KEY}_${prId}`;
  const list = JSON.parse(localStorage.getItem(key) || '[]') as PaymentRequest['history'];
  list!.push({
    id: `hist-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    actorName: user?.name || 'System',
    actorRole: user?.role || 'NV',
    action,
    fromStatus,
    toStatus,
    note,
    createdAt: new Date().toISOString(),
  });
  localStorage.setItem(key, JSON.stringify(list));
}

function readHistory(prId: string) {
  const key = `${HISTORY_KEY}_${prId}`;
  return JSON.parse(localStorage.getItem(key) || '[]') as PaymentRequest['history'];
}

function seedData(): PaymentRequest[] {
  const now = new Date();
  const iso = (offsetDays: number) => new Date(now.getTime() - offsetDays * 86400000).toISOString();
  return [
    {
      id: 'seed-001',
      code: 'PC-202608-0001',
      amount: 45000000,
      type: 'chi_thuong',
      urgency: true,
      status: 'PENDING_KTT',
      reason: 'Chi thanh toán hợp đồng NCC XYZ để duy trì dịch vụ vận hành hệ thống ERP',
      requesterId: 'dev-nv',
      requesterName: 'Trần Thị B',
      department: 'Marketing',
      supplier: 'XYZ Trading Co',
      createdAt: iso(1),
      submittedAt: iso(1),
      attachments: [{ id: 'a1', fileName: 'hop-dong-xyz.pdf', sizeBytes: 245000, mimeType: 'application/pdf' }],
    },
    {
      id: 'seed-002',
      code: 'PC-202608-0002',
      amount: 12500000,
      type: 'chi_thuong',
      urgency: false,
      status: 'PENDING_KTT',
      reason: 'Chi phí quảng cáo Facebook tháng 8 chiến dịch Back to School',
      requesterId: 'dev-nv',
      requesterName: 'Trần Thị B',
      department: 'Marketing',
      supplier: 'Facebook Ads',
      createdAt: iso(2),
      submittedAt: iso(2),
    },
    {
      id: 'seed-003',
      code: 'PC-202608-0003',
      amount: 8000000,
      type: 'chi_thuong',
      urgency: false,
      status: 'PENDING_KTT',
      reason: 'Mua thiết bị mạng bổ sung cho phòng server mới',
      requesterId: 'dev-nvkt',
      requesterName: 'Lê Huỳnh Diệu',
      department: 'Kỹ thuật',
      supplier: 'FPT Trading',
      createdAt: iso(3),
      submittedAt: iso(3),
    },
    {
      id: 'seed-004',
      code: 'PC-202608-0004',
      amount: 120000000,
      type: 'tam_ung',
      advanceType: 'planned',
      urgency: false,
      status: 'PENDING_CEO',
      reason: 'Tạm ứng công tác Nhật Bản tháng 9 - đã có kế hoạch approve từ tháng trước',
      requesterId: 'dev-ceo',
      requesterName: 'Phạm Minh Đức',
      department: 'Ban Giám đốc',
      createdAt: iso(4),
      submittedAt: iso(4),
      attachments: [{ id: 'a2', fileName: 'ke-hoach-cong-tac.pdf', sizeBytes: 512000, mimeType: 'application/pdf' }],
    },
    {
      id: 'seed-005',
      code: 'PC-202608-0005',
      amount: 15000000,
      type: 'tam_ung',
      advanceType: 'urgent',
      urgency: false,
      status: 'PENDING_KTT',
      reason: 'Tạm ứng khẩn cấp mua switch thay thế sau sự cố mất mạng phòng server',
      requesterId: 'dev-nvkt',
      requesterName: 'Lê Huỳnh Diệu',
      department: 'Kỹ thuật',
      createdAt: iso(0),
      submittedAt: iso(0),
    },
    {
      id: 'seed-006',
      code: 'PC-202608-0006',
      amount: 2500000,
      type: 'chi_thuong',
      urgency: false,
      status: 'DRAFT',
      reason: 'Chi phí văn phòng phẩm quý 3',
      requesterId: 'dev-nv',
      requesterName: 'Trần Thị B',
      department: 'Marketing',
      supplier: 'VP office supplies',
      createdAt: iso(0),
    },
    {
      id: 'seed-007',
      code: 'PC-202607-0089',
      amount: 3200000,
      type: 'chi_thuong',
      urgency: false,
      status: 'APPROVED',
      reason: 'Chi phí Grab đi lại tháng 7 - tổng hợp cuối tháng',
      requesterId: 'dev-nvkt',
      requesterName: 'Lê Huỳnh Diệu',
      department: 'Kế toán',
      supplier: 'Grab',
      createdAt: iso(14),
      submittedAt: iso(14),
    },
    {
      id: 'seed-008',
      code: 'PC-202607-0085',
      amount: 8500000,
      type: 'chi_thuong',
      urgency: false,
      status: 'REJECTED',
      reason: 'Chi phí ăn uống họp mặt tháng 7',
      requesterId: 'dev-nv',
      requesterName: 'Trần Thị B',
      department: 'Marketing',
      createdAt: iso(20),
      submittedAt: iso(19),
    },
    {
      id: 'seed-009',
      code: 'PC-202608-0007',
      amount: 3500000,
      type: 'hoan_ung',
      urgency: false,
      status: 'PENDING_KTT',
      reason: 'Hoàn ứng công tác Đà Nẵng T7: tạm ứng 15M, thực chi 11.5M, hoàn lại 3.5M kèm biên lai',
      requesterId: 'dev-nvkt',
      requesterName: 'Lê Huỳnh Diệu',
      department: 'Kỹ thuật',
      createdAt: iso(2),
      submittedAt: iso(2),
      attachments: [{ id: 'a3', fileName: 'bien-lai-cong-tac-danang.pdf', sizeBytes: 380000, mimeType: 'application/pdf' }],
    },
    {
      id: 'seed-010',
      code: 'PC-202607-0092',
      amount: 1800000,
      type: 'hoan_ung',
      urgency: false,
      status: 'APPROVED',
      reason: 'Hoàn ứng chi phí tiếp khách T7: tạm ứng 5M, thực chi 3.2M, hoàn lại 1.8M',
      requesterId: 'dev-nv',
      requesterName: 'Trần Thị B',
      department: 'Marketing',
      createdAt: iso(15),
      submittedAt: iso(15),
    },
    {
      id: 'seed-011',
      code: 'PC-202608-0008',
      amount: 4000000,
      type: 'thuong_nong',
      urgency: true,
      status: 'PENDING_KTT',
      reason: 'Thưởng nóng team Software đã go-live dự án ODI đúng hẹn — chia cho 4 NV',
      requesterId: 'dev-ktt',
      requesterName: 'Nguyễn Thị Hà',
      department: 'Kế toán',
      createdAt: iso(1),
      submittedAt: iso(1),
      recipients: [
        { employeeId: 'dev-ktt', employeeName: 'Nguyễn Thị Hà', amount: 1000000 },
        { employeeId: 'dev-nvkt', employeeName: 'Lê Huỳnh Diệu', amount: 1000000 },
        { employeeId: 'dev-ceo', employeeName: 'Phạm Minh Đức', amount: 1000000 },
        { employeeId: 'dev-nv', employeeName: 'Trần Thị B', amount: 1000000 },
      ],
    },
  ];
}

export const mockPaymentsApi = {
  async list(q: ListQuery): Promise<Paged<PaymentRequest>> {
    const user = currentUser();
    let items = readAll();

    // Filter theo role — NV chỉ thấy phiếu của mình
    if (user?.role === 'NV') {
      items = items.filter((i) => i.requesterId === user.id);
    }

    if (q.status) items = items.filter((i) => i.status === q.status);
    if (q.type) items = items.filter((i) => i.type === q.type);
    if (typeof q.urgency === 'boolean') items = items.filter((i) => i.urgency === q.urgency);
    if (q.search) {
      const s = q.search.toLowerCase();
      items = items.filter(
        (i) =>
          i.code.toLowerCase().includes(s) ||
          i.reason.toLowerCase().includes(s) ||
          (i.supplier || '').toLowerCase().includes(s) ||
          (i.requesterName || '').toLowerCase().includes(s),
      );
    }

    items.sort((a, b) => (b.createdAt > a.createdAt ? 1 : -1));

    const page = q.page || 1;
    const limit = q.limit || 20;
    const total = items.length;
    const paged = items.slice((page - 1) * limit, page * limit);

    return {
      items: paged,
      paging: {
        page,
        limit,
        total,
        totalPages: Math.ceil(total / limit),
        hasNextPage: page * limit < total,
      },
    };
  },

  async get(id: string): Promise<PaymentRequest> {
    const items = readAll();
    const found = items.find((i) => i.id === id);
    if (!found) throw new Error('Payment request not found');
    return { ...found, history: readHistory(id) };
  },

  async create(input: CreatePaymentInput): Promise<PaymentRequest> {
    const user = currentUser();
    if (!user) throw new Error('Chưa login');

    if (input.type === 'tam_ung' && !input.advanceType) {
      throw new Error('Tạm ứng bắt buộc chọn loại (có kế hoạch / đột xuất).');
    }
    if (input.type === 'tam_ung' && input.amount > 5_000_000 && !input.attachmentIds?.length) {
      throw new Error('Tạm ứng > 5M yêu cầu kèm quotation/plan.');
    }
    if (input.type === 'thuong_nong') {
      if (!input.recipients?.length) throw new Error('Thưởng nóng: chọn ít nhất 1 người nhận.');
      const sum = input.recipients.reduce((s, r) => s + r.amount, 0);
      if (sum !== input.amount) {
        throw new Error(
          `Tổng thưởng (${sum.toLocaleString('vi-VN')}đ) không khớp Số tiền phiếu (${input.amount.toLocaleString('vi-VN')}đ).`,
        );
      }
    }

    const items = readAll();
    const newItem: PaymentRequest = {
      id: `pr-${Date.now()}`,
      code: genCode(),
      amount: input.amount,
      type: input.type,
      advanceType: input.advanceType,
      urgency: input.urgency ?? false,
      status: 'DRAFT',
      reason: input.reason,
      requesterId: user.id,
      requesterName: user.name,
      department: input.department,
      supplier: input.supplier,
      bankAccount: input.bankAccount,
      createdAt: new Date().toISOString(),
      recipients: input.recipients?.map((r) => ({
        employeeId: r.employeeId,
        // Tra tên từ MOCK_EMPLOYEES nếu có, fallback id
        employeeName: MOCK_EMP_NAMES[r.employeeId] || r.employeeId,
        amount: r.amount,
        note: r.note ?? null,
      })),
    };
    items.unshift(newItem);
    writeAll(items);
    addHistory(newItem.id, 'create', null, 'DRAFT');
    return newItem;
  },

  async update(id: string, input: Partial<CreatePaymentInput>): Promise<PaymentRequest> {
    const items = readAll();
    const idx = items.findIndex((i) => i.id === id);
    if (idx === -1) throw new Error('Not found');
    if (items[idx].status !== 'DRAFT' && items[idx].status !== 'PENDING_NVKT' && items[idx].status !== 'PENDING_KTT') {
      throw new Error(`Chỉ sửa được khi DRAFT, PENDING_NVKT hoặc PENDING_KTT. Hiện tại: ${items[idx].status}`);
    }

    const { recipients: recIn, ...rest } = input;
    Object.assign(items[idx], rest);
    if (recIn) {
      items[idx].recipients = recIn.map((r) => ({
        employeeId: r.employeeId,
        employeeName: MOCK_EMP_NAMES[r.employeeId] || r.employeeId,
        amount: r.amount,
        note: r.note ?? null,
      }));
    }
    writeAll(items);
    return items[idx];
  },

  async remove(id: string): Promise<{ ok: true }> {
    const items = readAll();
    const idx = items.findIndex((i) => i.id === id);
    if (idx === -1) throw new Error('Not found');
    if (items[idx].status !== 'DRAFT' && items[idx].status !== 'CANCELLED') {
      throw new Error(`Chỉ xoá được khi DRAFT hoặc CANCELLED. Hiện tại: ${items[idx].status}`);
    }
    items.splice(idx, 1);
    writeAll(items);
    return { ok: true };
  },

  async submit(id: string): Promise<PaymentRequest> {
    const items = readAll();
    const idx = items.findIndex((i) => i.id === id);
    if (idx === -1) throw new Error('Not found');
    if (items[idx].status !== 'DRAFT') throw new Error(`Chỉ submit từ DRAFT`);

    items[idx].status = 'PENDING_NVKT';
    items[idx].submittedAt = new Date().toISOString();
    writeAll(items);
    addHistory(id, 'submit', 'DRAFT', 'PENDING_NVKT');
    return items[idx];
  },

  async disburse(id: string): Promise<PaymentRequest> {
    const items = readAll();
    const idx = items.findIndex((i) => i.id === id);
    if (idx === -1) throw new Error('Not found');
    if (items[idx].status !== 'APPROVED') throw new Error(`Chỉ chi được từ APPROVED. Hiện tại: ${items[idx].status}`);
    items[idx].status = 'DISBURSED';
    items[idx].disbursedAt = new Date().toISOString();
    writeAll(items);
    addHistory(id, 'disburse', 'APPROVED', 'DISBURSED');
    return items[idx];
  },

  async cancel(id: string): Promise<PaymentRequest> {
    const items = readAll();
    const idx = items.findIndex((i) => i.id === id);
    if (idx === -1) throw new Error('Not found');
    if (items[idx].status !== 'PENDING_NVKT' && items[idx].status !== 'PENDING_KTT') {
      throw new Error(`Chỉ huỷ được khi PENDING_KTT. Hiện tại: ${items[idx].status}`);
    }
    const previousStatus = items[idx].status;
    items[idx].status = 'CANCELLED';
    writeAll(items);
    addHistory(id, 'cancel', previousStatus, 'CANCELLED');
    return items[idx];
  },
};

/** Mock cho đúng state machine: NV.KT lập PC xong mới chuyển phiếu qua hàng KTT. */
export async function mockCreateCashVoucher(paymentRequestId: string, input: CreateCashVoucherInput): Promise<CashVoucher> {
  const items = readAll();
  const index = items.findIndex((item) => item.id === paymentRequestId);
  if (index === -1) throw new Error('Không tìm thấy đề nghị chi.');
  const request = items[index];
  if (request.status !== 'PENDING_NVKT') throw new Error(`Chỉ lập phiếu chi khi chờ NV.KT. Hiện tại: ${request.status}`);
  const user = currentUser();
  const now = new Date().toISOString();
  const code = `PC-${new Date().toISOString().slice(0, 7).replace('-', '')}-${String(items.filter((item) => item.cashVoucher).length + 1).padStart(4, '0')}`;
  const voucher: CashVoucher = {
    id: `mock-voucher-${Date.now()}`,
    code,
    paymentRequestId,
    method: input.method,
    voucherDate: input.voucherDate,
    documentDate: input.documentDate,
    bankAccountUsed: input.bankAccountUsed ?? null,
    uncNumber: input.uncNumber ?? null,
    actualAmount: input.actualAmount,
    receiverType: input.receiverType,
    receiverName: input.receiverName,
    receiverIdNumber: input.receiverIdNumber ?? null,
    note: input.note ?? null,
    debitAccount: input.debitAccount ?? (request.type === 'tam_ung' || request.type === 'hoan_ung' ? '141' : request.type === 'thuong_nong' ? '334' : '6421'),
    creditAccount: input.method === 'chuyen_khoan' ? '1121' : input.method === 'tien_mat_usd' ? '1112' : '1111',
    createdBy: user?.id ?? 'dev-nvkt',
    createdByName: user?.name ?? 'NV.KT',
    createdAt: now,
  };
  items[index] = { ...request, status: 'PENDING_KTT', cashVoucher: voucher };
  writeAll(items);
  addHistory(paymentRequestId, 'prepare', 'PENDING_NVKT', 'PENDING_KTT', `Đã lập ${code}`);
  return voucher;
}
